import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef } from "react";

import { colors } from "../../utils.ts";

export const LoadingDNA = ({ isLoading }: { isLoading: boolean }) => {
  return (
    <AnimatePresence>
      {!!isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "transparent",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            pointerEvents: "none",
            paddingRight: 15,
          }}
          data-testid="loading-dna"
        >
          <LoadingAnimation />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const LoadingAnimation = () => {
  const ref = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(undefined);

  const drawLoadingAnimation = useCallback((ctx: CanvasRenderingContext2D, frame: number) => {
    const canvas = ctx.canvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const baseY = canvas.height / 2;
    const amplitude = 30;
    const frequency = 0.02;
    const speed = 0.05;

    for (let i = 0; i < canvas.width; i += 4) {
      const y1 = baseY + Math.sin(i * frequency + frame * speed) * amplitude;
      const y2 = baseY + Math.sin((i + 100) * frequency + frame * speed) * amplitude;

      ctx.beginPath();
      ctx.strokeStyle = colors.primaryMain;
      ctx.lineWidth = 2;
      ctx.moveTo(i, y1);
      ctx.lineTo(i + 4, y1);
      ctx.stroke();

      ctx.beginPath();
      ctx.strokeStyle = colors.successMain;
      ctx.moveTo(i, y2);
      ctx.lineTo(i + 4, y2);
      ctx.stroke();

      if (i % 20 === 0 && i > 0) {
        ctx.beginPath();
        ctx.strokeStyle = colors.secondaryLight;
        ctx.lineWidth = 1;
        ctx.moveTo(i, y1);
        ctx.lineTo(i, y2);
        ctx.stroke();
      }
    }
  }, []);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 200;
    canvas.height = 120;

    let frame = 0;
    const animate = () => {
      drawLoadingAnimation(ctx, frame);
      frame = frame + 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [drawLoadingAnimation]);

  return <canvas ref={ref} width={200} height={120} style={{ width: 100, height: 60 }} />;
};
